import React, { useContext, useEffect, useState } from "react";
import { Breadcrumb } from "react-bootstrap";
import { Button} from '@blueprint/ui';
import { NavLink, useHistory } from "react-router-dom";

import { useClaimList } from "../../api/claim";
import { useInvoiceSummary } from "../../api/invoice";
import { ProjectAvatar } from "../../common/components";
import { Loader } from "../../common/components/core";
import { useSelector, useDispatch } from "../../redux/hooks";
import { currentRolesSelector } from "../../redux/slices/auth/selectors";
import { getContactByAccountName } from "../../redux/slices/accountInfo/thunks";
import { BesuClaimListItem } from "../../types/claim";
import { InvoiceSummary } from "../../types/invoice";
import { BesuProjectState, ProjectStatus } from "../../types/project";
import { PageTitle } from "../../types/pageTitle";
import { getNameInitial } from "../../utils/common";
import { isNodeDeveloper, isUserContractor, isUserPO } from "../../utils/role";
import ClaimList from "../claim/components/ClaimList";

import ApproverList from "./components/ApproverList";
import ProjectDetailCard from "./components/ProjectDetailCard";
import useConfirmDeleteProjectHandler from "./hooks/useConfirmDeleteProjectHandler";
import useDeleteProjectHandler from "./hooks/useDeleteProjectHandler";
import ProjectContext from "./ProjectContext";
import { Contact } from "../../types/common";

const ProjectDetailView = (): JSX.Element => {
    document.title = PageTitle.ProjectDetailView;
    const { data: project } = useContext(ProjectContext);
    const { data: claims, isValidating: isClaimListLoading } = useClaimList(project.referenceId);

    const currentRoles = useSelector(currentRolesSelector);
    const [currentContact, setCurrentContact] = useState<Contact>();
    const [isFetchingContact, setIsFetchingContact] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        const getAccountName = () => {
            switch (true) {
                case !isNodeDeveloper():
                    return project.developer?.displayName ?? "";
                case !isUserContractor(currentRoles):
                    return project.mainContractors[0]?.displayName ?? "";
                default:
                    return project.architect?.accountName ?? "";
            }
        };

        const getContactInfo = async () => {
            setIsFetchingContact(true);
            const contactResponse = await dispatch(getContactByAccountName(getAccountName())).unwrap();
            const contact: Contact = {
                company: contactResponse.name,
                email: contactResponse.contactEmail,
                contactPerson: contactResponse.contactPerson,
                phone: contactResponse.contactNumber,
            };

            setCurrentContact(contact);
            setIsFetchingContact(false);
        };

        getContactInfo();
    }, [currentRoles, project, dispatch]);

    const { data: invoiceSummary, isValidating: isSummaryLoading } = useInvoiceSummary(project.referenceId);
    return (
        <Loader
            isLoading={
                isClaimListLoading || isSummaryLoading || isFetchingContact
            }
        >
            {claims && invoiceSummary && (
                <PureProjectDetailView
                    projectRef={project.referenceId}
                    project={project}
                    claims={claims}
                    invoiceSummary={invoiceSummary}
                    currentContact={currentContact}
                />
            )}
        </Loader>
    );
};

type PureProjectDetailProps = {
    projectRef: string;
    project: BesuProjectState;
    claims: BesuClaimListItem[];
    invoiceSummary: InvoiceSummary;
    currentContact?: Contact;
};

export const PureProjectDetailView = ({
    projectRef,
    project,
    claims,
    invoiceSummary,
    currentContact,
}: PureProjectDetailProps): JSX.Element => {
    const { handler: deleteProject } = useDeleteProjectHandler(
        projectRef,
        project,
    );
    const { handler: confirmDeleteProject } =
        useConfirmDeleteProjectHandler(projectRef);
    const currentRoles = useSelector(currentRolesSelector);

    const history = useHistory();

    const editProject = () => {
        history.push(`/project/${project.referenceId}/edit`);
    };
    useEffect(() => {
        if (project.status === ProjectStatus.PENDING_DELETE) {
            confirmDeleteProject();
        }
    }, [confirmDeleteProject, currentRoles, project.status]);

    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/project">Project List</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    {project.name}
                </Breadcrumb.Item>
            </Breadcrumb>

            <div className="d-flex align-items-center mb-5">
                <ProjectAvatar className="text-center">
                    {getNameInitial(project.name)}
                </ProjectAvatar>
                <h1 className="flex-grow-1 mb-0 ms-3">
                    {project.name}{" "}
                    <span className="fs-6 fw-bold">{project.externalProjectId}</span>
                </h1>

                {isUserPO(currentRoles) &&
                project.claimReferenceIds.length === 0 &&
                project.bankAccountRefs.length === 0 ? (
                    <>
                        <Button
                            variant="light.tertiary"
                            onClick={deleteProject}
                        >
                            Delete Project
                        </Button>
                        <Button
                            variant="light.primary"
                            onClick={editProject}
                            style={{marginLeft: '10px'}}
                        >
                            Edit
                        </Button>
                    </>
                ) : (
                    isUserPO(currentRoles) && (
                        <Button
                            variant="light.tertiary"
                            onClick={editProject}
                        >
                            Edit
                        </Button>
                    )
                )}

                {isUserContractor(currentRoles) && (
                    <NavLink to={`/project/${project.referenceId}/claim/new`}>
                        <Button variant="light.primary" className="ms-4">
                            New Claim
                        </Button>
                    </NavLink>
                )}
            </div>

            <ProjectDetailCard
                project={project}
                invoiceSummary={invoiceSummary}
                claims={claims}
                currentContact={currentContact}
            />
            <h2>Approver List</h2>
            <ApproverList
                projectClaimApprovers={project.claimApprovers}
                projectPaymentOrderApprovers={project.paymentOrderApprovers}
            />
            <h2>Claim Submissions</h2>
            <ClaimList project={project} claims={claims} />
        </>
    );
};

export default ProjectDetailView;
